import { useEffect, useState } from "react";
import { Copy, Trash2, Loader2, CloudUpload, RefreshCw, Image as ImageIcon, Film, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MediaUpload } from "@/components/MediaUpload";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const mb = (b) => (b ? `${(b / 1024 / 1024).toFixed(2)} MB` : "—");

export default function AdminMedia() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [ext, setExt] = useState(null);
  const [type, setType] = useState("image");
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [del, setDel] = useState(null);

  const loadStatus = () => {
    api.get("/admin/media/status").then(({ data }) => setStatus(data));
    api.get("/admin/media/external-count").then(({ data }) => setExt(data.external_media));
  };
  const loadItems = (more = false) => {
    if (!more) setItems(null);
    api.get("/admin/media", { params: { resource_type: type, cursor: more ? cursor : "" } })
      .then(({ data }) => { setItems((p) => (more ? [...(p || []), ...data.items] : data.items)); setCursor(data.next_cursor); })
      .catch((e) => { setItems([]); toast.error(e?.response?.data?.detail || "Could not load media"); });
  };
  useEffect(loadStatus, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (status?.connected) loadItems(); }, [type, status?.connected]);
  useEffect(() => {
    if (status?.migration?.state !== "running") return undefined;
    const t = setInterval(loadStatus, 3000);
    return () => clearInterval(t);
  }, [status?.migration?.state]);

  const migrate = async () => {
    try { await api.post("/admin/media/migrate"); toast.success("Moving external media to Cloudinary…"); loadStatus(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };
  const remove = async () => {
    try { await api.delete("/admin/media", { params: { public_id: del.public_id, resource_type: del.resource_type } }); toast.success("Deleted from Cloudinary"); loadItems(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setDel(null);
  };
  const mig = status?.migration;

  return (
    <div className="space-y-6" data-testid="admin-media-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div><h1 className="font-display text-3xl font-bold">Media Library</h1><p className="text-sm text-[#B91C1C]">Every image, video and document is stored and served by Cloudinary.</p></div>
        {status?.connected && <MediaUpload folder={`uploads/${type === "video" ? "videos" : "images"}`} accept={type === "video" ? "video/*" : type === "raw" ? "application/pdf" : "image/*"} label="Upload new" onUploaded={() => loadItems()} testid="media-upload-btn" />}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-[#FECACA] p-4" data-testid="media-status-card">
          <div className="text-[10px] uppercase text-[#B91C1C]">Cloudinary</div>
          {!status ? <Loader2 className="w-4 h-4 animate-spin" /> : status.connected
            ? <div className="font-semibold text-emerald-700">Connected · {status.cloud_name}</div>
            : <div className="font-semibold text-red-600">{status.configured ? `Error: ${status.error}` : "Not configured"}</div>}
          {status?.usage && <div className="text-xs text-[#64748B] mt-1">Plan {status.usage.plan} · {status.usage.resources} files · {mb(status.usage.storage_bytes)} stored</div>}
        </div>
        <div className="bg-white rounded-2xl border border-[#FECACA] p-4">
          <div className="text-[10px] uppercase text-[#B91C1C]">Media still hosted outside Cloudinary</div>
          <div className="font-numbers text-xl font-bold" data-testid="media-external-count">{ext ?? "…"}</div>
        </div>
        <div className="bg-white rounded-2xl border border-[#FECACA] p-4 space-y-2">
          <div className="text-[10px] uppercase text-[#B91C1C]">Move everything to Cloudinary</div>
          {mig && <div className="text-xs" data-testid="media-migration-status">{mig.state === "running" ? `Running (${mig.progress || "starting"})…` : "Last run done"} · {mig.moved} moved · {mig.failed} failed</div>}
          {isSuper && status?.connected && <button onClick={migrate} disabled={mig?.state === "running" || ext === 0} className="btn-primary text-xs disabled:opacity-50" data-testid="media-migrate-btn"><CloudUpload className="w-4 h-4" /> Re-host external media</button>}
        </div>
      </div>

      {status?.connected && (
        <div className="space-y-4">
          <div className="flex gap-2">
            {[["image", ImageIcon, "Images"], ["video", Film, "Videos"], ["raw", FileText, "Documents"]].map(([k, I, l]) => (
              <button key={k} onClick={() => setType(k)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm border ${type === k ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#FECACA]"}`} data-testid={`media-tab-${k}`}><I className="w-4 h-4" /> {l}</button>
            ))}
            <button onClick={() => loadItems()} className="ml-auto inline-flex items-center gap-1 text-sm" data-testid="media-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</button>
          </div>
          {!items ? <Loader2 className="w-5 h-5 animate-spin" /> : items.length === 0 ? <p className="text-sm text-[#94A3B8] py-10 text-center" data-testid="media-empty">No files yet.</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="media-grid">
              {items.map((m) => (
                <div key={m.public_id} className="bg-white rounded-2xl border border-[#FECACA] overflow-hidden group">
                  <div className="aspect-square bg-[#FEF2F2] flex items-center justify-center">
                    {type === "image" ? <img src={m.url} alt="" loading="lazy" className="w-full h-full object-cover" /> : type === "video" ? <video src={m.url} className="w-full h-full object-cover" muted /> : <FileText className="w-10 h-10 text-[#B91C1C]" />}
                  </div>
                  <div className="p-2 text-[11px] space-y-1">
                    <div className="truncate font-mono" title={m.public_id}>{m.public_id.replace("evergro/", "")}</div>
                    <div className="text-[#94A3B8]">{m.width ? `${m.width}×${m.height} · ` : ""}{mb(m.bytes)}</div>
                    <div className="flex gap-1">
                      <button onClick={() => { navigator.clipboard.writeText(m.url); toast.success("URL copied"); }} className="flex-1 inline-flex justify-center items-center gap-1 py-1 rounded border border-[#FECACA]" data-testid="media-copy-btn"><Copy className="w-3 h-3" /> URL</button>
                      {isSuper && <button onClick={() => setDel(m)} className="px-2 py-1 rounded border border-red-200 text-red-600" data-testid="media-delete-btn"><Trash2 className="w-3 h-3" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {cursor && <button onClick={() => loadItems(true)} className="btn-outline text-sm" data-testid="media-load-more">Load more</button>}
        </div>
      )}

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this file from Cloudinary?</AlertDialogTitle>
            <AlertDialogDescription>Any page still using this URL will show a broken image. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Keep</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-[#DC2626]" data-testid="media-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
